import { Point_Change_Type, PointChangeFragment } from '@occc/graphql';

import { routeCreator as loyaltyEventRouteCreator } from '@occc-app/routes/LoyaltyEvent/utils';
import { routeCreator as awardRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import { routeCreator } from '@occc-app/routes/Transactions/utils';
import {
  EarningPointChange,
  ExpirationPointChange,
  ManualAdjustmentPointChange,
  MembershipPointDonationPointChange,
  OfferPurchasePointChange,
  PointChangeViewItemData,
  RedemptionCancelledPointChange,
  SourceTypename,
} from './types';

const sourceTypenameMap: Record<SourceTypename, string> = {
  LoyaltyEvent: 'Loyalty Event',
  MembershipPointDonation: 'Donation',
  ActivateTransaction: 'Offer Fulfillment',
  Chain: 'Point Expiration',
  PointChange: 'Redemption Cancelled',
  OfferPurchase: 'Reward Redemption',
  CommandCenterUser: 'Manual Adjustment',
  ActivateUser: 'Manual Adjustment',
  GameSession: 'Game Session',
  PromocodeUseRequest: 'Promo Code Offer',
  StoredValueTransaction: 'Stored Value Transaction', // TODO: Implement
};

const pointChangeTypeMap: { [key in Point_Change_Type]?: string } = {
  [Point_Change_Type.ReferralPointAward]: 'Referral Program Fulfillment',
  [Point_Change_Type.PointExpiration]: 'Point Expiration',
  [Point_Change_Type.MembershipPointDonation]: 'Donation',
};

export const getPointChangeSourceName = (params: {
  type: Point_Change_Type | null | undefined;
  sourceTypename: SourceTypename | undefined;
}): string => {
  const { type, sourceTypename } = params;
  // "type" field was added after the "source" implementation
  // and should be used in future for determining different types of point changes
  if (type && pointChangeTypeMap[type]) {
    // non-null assertion used on purpose
    // should be fixed when TS "strict" enabled
    return pointChangeTypeMap[type]!;
  }

  if (sourceTypename) {
    return sourceTypenameMap[sourceTypename];
  }

  return '';
};

export const getPointChangeItem = (pointChange: PointChangeFragment): PointChangeViewItemData => {
  const { id, type, offer, source, quantity } = pointChange;

  const item: PointChangeViewItemData = {
    id,
    type,
    sourceType: source?.__typename,
    name: getPointChangeSourceName({
      type: type,
      sourceTypename: source?.__typename,
    }),
    amount: quantity,
  };

  if (offer) {
    item.offer = {
      id: offer.id,
      name: offer.name,
      title: offer.marketingContents[0]?.title || offer.name,
      startDate: offer.publishAt ? new Date(offer.publishAt) : undefined,
      endDate: offer.expireAt ? new Date(offer.expireAt) : undefined,
      url: isLoyaltyEventPointChange(pointChange)
        ? loyaltyEventRouteCreator.details(offer.id)
        : offer.campaign?.uuid
        ? routeCreator.campaignOfferDetails(offer.campaign?.uuid, offer.id)
        : awardRouteCreator.details(offer.id),
    };
  }

  if (isOfferPurchasePointChange(pointChange)) {
    const award = pointChange.source.voucherGrantFor;
    item.award = {
      id: award.id,
      name: award.name,
      title: award.marketingContents[0]?.title || award.name,
      startDate: award.publishAt ? new Date(award.publishAt) : undefined,
      endDate: award.expireAt ? new Date(award.expireAt) : undefined,
      url: awardRouteCreator.details(award.id),
    };
  }

  if (isMembershipPointDonationPointChange(pointChange)) {
    item.donation = {
      bucketName: pointChange.source.bucket.name,
      pointsSpent: pointChange.source.pointsSpent,
      donationAmountCents: pointChange.source.donationAmountCents,
    };
  }

  return item;
};

export const isManualAdjustmentPointChange = (
  pointChange?: PointChangeFragment | null,
): pointChange is ManualAdjustmentPointChange => {
  if (pointChange?.source) {
    return (
      pointChange.source.__typename === 'ActivateUser' ||
      pointChange.source.__typename === 'CommandCenterUser'
    );
  }
  return false;
};

export const isLoyaltyEventPointChange = (
  fragment?: PointChangeFragment | null,
): fragment is EarningPointChange => {
  const typename = fragment?.source?.__typename;
  return typename === 'LoyaltyEvent';
};

export const isEarningPointChange = (
  fragment?: PointChangeFragment | null,
): fragment is EarningPointChange => {
  const typename = fragment?.source?.__typename;
  return typename === 'ActivateTransaction' || typename === 'LoyaltyEvent';
};

export const isRedemptionCancelledPointChange = (
  fragment?: PointChangeFragment | null,
): fragment is RedemptionCancelledPointChange => {
  return fragment?.source?.__typename === 'PointChange';
};

export const isOfferPurchasePointChange = (
  fragment?: PointChangeFragment | null,
): fragment is OfferPurchasePointChange => {
  return fragment?.source?.__typename === 'OfferPurchase';
};

export const isMembershipPointDonationPointChange = (
  fragment?: PointChangeFragment | null,
): fragment is MembershipPointDonationPointChange => {
  return fragment?.source?.__typename === 'MembershipPointDonation';
};

export const isExpirationPointChange = (
  fragment?: PointChangeFragment | null,
): fragment is ExpirationPointChange => {
  return fragment?.source?.__typename === 'Chain';
};

export const isPromocodeUseRequestPointChange = (
  fragment?: PointChangeFragment | null,
): fragment is ExpirationPointChange => {
  return fragment?.source?.__typename === 'PromocodeUseRequest';
};
